// VideoCompositor: draws the program (preview) frame at a given transport
// position by compositing every video track top-down, and keeps each clip's
// <video> element slaved to the audio transport clock (seek when paused,
// play + drift-correct when playing). The per-clip draw math is exported so the
// offline MP4 exporter composites frames identically.
import { Store } from "../state/store";
import { Clip, IDENTITY_TRANSFORM, Project, clipSpeed, sourceTimeAt } from "../state/model";
import { transitionAt } from "../state/transitions";
import { generatorFrame } from "./generators";
import { makeThumbnailVideo } from "./source";

const DRIFT_PLAY = 0.35; // s — only hard-reseek a playing element on big drift
                         //       (frequent reseeks cause black flashes; let it play)
const DRIFT_SEEK = 0.02; // s — reseek tolerance while paused/scrubbing
const MIN_FADE = 0.02;   // s — shorter fades (e.g. the 5ms audio de-click default) are ignored for video

/** Draw one video frame into frame-space coordinates (0,0)-(frameW,frameH),
 *  applying the clip's crop, contain-fit, scale, rotation and opacity. `alpha`
 *  multiplies the clip opacity (used for fades). Shared by the live monitor and
 *  the offline exporter. */
export function drawClipFrame(
  g: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  source: CanvasImageSource,
  srcW: number, srcH: number,
  frameW: number, frameH: number,
  clip: Clip,
  alpha = 1,
) {
  if (!srcW || !srcH) return;
  const t = clip.transform ?? IDENTITY_TRANSFORM;
  const c = clip.crop;
  let sx = 0, sy = 0, sw = srcW, sh = srcH;
  if (c) {
    sx = c.l * srcW; sy = c.t * srcH;
    sw = srcW * (1 - c.l - c.r); sh = srcH * (1 - c.t - c.b);
    if (sw <= 0 || sh <= 0) return;
  }
  const a = Math.max(0, Math.min(1, t.opacity * alpha));
  if (a <= 0.001) return;
  // contain-fit the (cropped) source into the frame, then apply user scale.
  const fit = Math.min(frameW / sw, frameH / sh);
  const drawW = sw * fit * t.scale;
  const drawH = sh * fit * t.scale;
  const cx = frameW / 2 + t.x * frameW;
  const cy = frameH / 2 + t.y * frameH;
  g.save();
  g.globalAlpha = a;
  g.translate(cx, cy);
  if (t.rotation) g.rotate((t.rotation * Math.PI) / 180);
  g.drawImage(source, sx, sy, sw, sh, -drawW / 2, -drawH / 2, drawW, drawH);
  g.restore();
}

/** Fade-in/out opacity multiplier for a video clip at timeline position `pos`. */
export function fadeAlpha(clip: Clip, pos: number): number {
  const into = pos - clip.start, left = clip.start + clip.duration - pos;
  let a = 1;
  if (clip.fadeIn >= MIN_FADE && into < clip.fadeIn) a = Math.min(a, Math.max(0, into / clip.fadeIn));
  if (clip.fadeOut >= MIN_FADE && left < clip.fadeOut) a = Math.min(a, Math.max(0, left / clip.fadeOut));
  return a;
}

/** The clip on a track visible at timeline position `pos`: active, enabled, and
 *  — where clips overlap — the one that starts latest (it sits on top). */
export function clipAt(clips: Clip[], pos: number): Clip | undefined {
  let best: Clip | undefined;
  for (const c of clips) {
    if (c.takeActive === false || c.disabled) continue;
    if (pos >= c.start && pos < c.start + c.duration && (!best || c.start >= best.start)) best = c;
  }
  return best;
}

/** One clip to draw on a track at a moment, with its blend alpha and the source
 *  time to show. Inside a cross-dissolve there are two: the outgoing clip (held
 *  on its last frame if its media runs out) under the incoming one fading in. */
export interface Layer { clip: Clip; alpha: number; time: number; layer: number; hold: boolean; }

export function layersAt(clips: Clip[], pos: number, mediaDur: (c: Clip) => number): Layer[] {
  const w = transitionAt(clips, pos);
  if (w) {
    const p = Math.max(0, Math.min(1, (pos - w.t0) / (w.t1 - w.t0)));
    const layer = (c: Clip, alpha: number, i: number): Layer => {
      const t = sourceTimeAt(c, pos);
      const held = Math.max(0, Math.min(Math.max(0, mediaDur(c) - 0.001), t));
      return { clip: c, alpha, time: held, layer: i, hold: Math.abs(held - t) > 1e-6 };
    };
    return [layer(w.a, 1, 0), layer(w.b, p, 1)];
  }
  const c = clipAt(clips, pos);
  return c ? [{ clip: c, alpha: fadeAlpha(c, pos), time: sourceTimeAt(c, pos), layer: 0, hold: false }] : [];
}

interface FrameCache { cv: HTMLCanvasElement; g: CanvasRenderingContext2D; has: boolean; }

export class VideoCompositor {
  private c2d: CanvasRenderingContext2D;
  // Per-element snapshot of the last good frame, taken right before a seek, so a
  // seeking element (no current frame) draws the previous frame instead of black.
  private frameCache = new Map<HTMLVideoElement, FrameCache>();
  // Extra elements for when one asset is visible twice at once (two tracks, or
  // both sides of a dissolve) — one element can only show one time.
  private pool = new Map<string, HTMLVideoElement[]>(); // assetId -> extras
  // Which clip each element showed last frame, so a clip keeps its element across
  // frames (no re-seek when a dissolve hands over from one clip to the next).
  private owner = new Map<HTMLVideoElement, string>();
  private dirty = true;
  private lastPos = -1;

  constructor(private store: Store, public canvas: HTMLCanvasElement) {
    this.c2d = canvas.getContext("2d", { alpha: false })!;
    store.on("change", () => { this.dirty = true; });
  }

  /** Force a redraw on the next render (e.g. after the canvas is resized). */
  invalidate() { this.dirty = true; }

  private cacheFor(el: HTMLVideoElement): FrameCache {
    const w = el.videoWidth, h = el.videoHeight;
    let c = this.frameCache.get(el);
    if (!c || c.cv.width !== w || c.cv.height !== h) {
      const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
      c = { cv, g: cv.getContext("2d")!, has: false };
      this.frameCache.set(el, c);
    }
    return c;
  }

  private snapshot(el: HTMLVideoElement) {
    if (el.readyState < 2 || el.seeking || !el.videoWidth) return;
    const c = this.cacheFor(el);
    try { c.g.drawImage(el, 0, 0); c.has = true; } catch {}
  }

  /** The element to show `clip` this frame: the one it had last frame if free,
   *  else one not wanted by another visible clip, else a new pooled element. */
  private elementFor(clip: Clip, taken: Set<HTMLVideoElement>, visible: Set<string>): HTMLVideoElement | null {
    const src = this.store.videos.get(clip.assetId);
    if (!src) return null;
    let extras = this.pool.get(clip.assetId);
    if (!extras) { extras = []; this.pool.set(clip.assetId, extras); }
    const all = [src.el, ...extras].filter((e) => !taken.has(e));
    let el = all.find((e) => this.owner.get(e) === clip.id)
      ?? all.find((e) => { const o = this.owner.get(e); return !o || !visible.has(o); });
    if (!el) { el = makeThumbnailVideo(src.url); extras.push(el); }
    this.owner.set(el, clip.id);
    return el;
  }

  /** Draw the composited frame at `pos` and sync the video elements to it. */
  render(pos: number, playing: boolean) {
    const g = this.c2d;
    const cw = this.canvas.width, ch = this.canvas.height;
    if (cw === 0 || ch === 0) return;
    // Paused on the same frame with nothing changed and every element settled:
    // the canvas already shows it, so skip the redraw.
    if (!playing && !this.dirty && pos === this.lastPos) return;
    this.lastPos = pos;
    const p: Project = this.store.project;
    const fw = p.width, fh = p.height;

    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = "#000";
    g.fillRect(0, 0, cw, ch);
    const k = cw / fw; // uniform — the canvas backing store preserves frame aspect
    g.setTransform(k, 0, 0, k, 0, 0);

    const used = new Set<HTMLVideoElement>();
    let settled = true;
    const mediaDur = (c: Clip) => this.store.asset(c.assetId)?.duration || Infinity;
    // Top-down: tracks earlier in the list paint last (on top), so iterate in reverse.
    const tracks = p.tracks;
    const perTrack: Layer[][] = [];
    const visible = new Set<string>();
    for (let i = tracks.length - 1; i >= 0; i--) {
      const track = tracks[i];
      if (track.kind !== "video" || track.disabled) continue;
      const layers = layersAt(track.clips, pos, mediaDur);
      perTrack.push(layers);
      for (const l of layers) visible.add(l.clip.id);
    }
    for (const layers of perTrack) {
      for (const { clip, alpha, time, hold } of layers) {
        const gen = this.store.asset(clip.assetId)?.generator;
        if (gen) { // generated media: drawn from its spec at sequence resolution
          drawClipFrame(g, generatorFrame(`${clip.assetId}|${clip.id}`, gen, fw, fh, time, p.fps), fw, fh, fw, fh, clip, alpha);
          continue;
        }
        const el = this.elementFor(clip, used, visible);
        if (!el) continue;
        used.add(el);
        this.syncEl(el, time, playing && !hold, clipSpeed(clip)); // a held frame stays paused
        const vw = el.videoWidth, vh = el.videoHeight;
        if (!vw || !vh) { settled = false; continue; }
        if (el.readyState >= 2 && !el.seeking) drawClipFrame(g, el, vw, vh, fw, fh, clip, alpha);
        else {
          settled = false;
          const cache = this.frameCache.get(el);
          if (cache?.has) drawClipFrame(g, cache.cv, vw, vh, fw, fh, clip, alpha);
        }
      }
    }
    this.dirty = !settled; // keep redrawing until every visible element has its frame

    // Pause any video element that isn't contributing to this frame.
    const pauseIdle = (el: HTMLVideoElement) => { if (!used.has(el) && !el.paused) el.pause(); };
    for (const vs of this.store.videos.values()) pauseIdle(vs.el);
    for (const list of this.pool.values()) for (const el of list) pauseIdle(el);
  }

  /** Keep one element aligned to the transport: play + drift-correct, or seek. */
  private syncEl(el: HTMLVideoElement, expected: number, playing: boolean, speed: number) {
    const t = Math.max(0, expected);
    const seek = () => { this.snapshot(el); try { el.currentTime = t; } catch {} };
    if (playing) {
      if (el.playbackRate !== speed) { try { el.playbackRate = speed; } catch {} }
      if (el.paused) { seek(); el.play().catch(() => {}); }
      else if (Math.abs(el.currentTime - t) > DRIFT_PLAY * Math.max(1, speed)) seek();
    } else {
      if (!el.paused) el.pause();
      if (Math.abs(el.currentTime - t) > DRIFT_SEEK) seek();
    }
  }

  /** Pause all elements (e.g. on transport stop). */
  pauseAll() {
    for (const vs of this.store.videos.values()) { try { vs.el.pause(); } catch {} }
    for (const list of this.pool.values()) for (const el of list) { try { el.pause(); } catch {} }
  }
}
